"use client";

import Link from "next/link";
import { ArrowUpRight } from "lucide-react";
import type { BuzzBusiness, BuzzDeal, BuzzEvent } from "@/components/local-buzz/types";

export function EventTable({ events, basePath = "/events" }: { events: BuzzEvent[]; basePath?: string }) {
  const hrefFor = (slug: string) => `${basePath}/${slug}`;

  return (
    <>
      <div className="hidden overflow-hidden rounded-xl border border-border bg-card md:block">
        <div className="grid grid-cols-[110px_1.4fr_1fr_1fr_120px_140px] gap-4 border-b border-border bg-ink-2/60 px-5 py-3 text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
          <div>Date</div><div>Event</div><div>Location</div><div>Time</div><div>Price</div><div>Booking</div>
        </div>
        {events.map((event) => (
          <Link key={event.id} href={hrefFor(event.slug)} className="group grid grid-cols-[110px_1.4fr_1fr_1fr_120px_140px] items-center gap-4 border-b border-border px-5 py-4 text-sm transition last:border-b-0 hover:bg-ink-2/40">
            <div className="font-display text-base font-black text-brand">{event.dateShort}</div>
            <div className="min-w-0">
              <div className="font-display text-base font-black leading-tight">{event.title}</div>
              <div className="line-clamp-1 text-xs text-muted-foreground">{event.lockedTeaser ? "Members-only detail" : event.tagline}</div>
            </div>
            <div className="text-muted-foreground"><div className="text-foreground">{event.venue}</div><div className="text-xs">{event.suburb}</div></div>
            <div className="text-muted-foreground">{event.timeLabel}</div>
            <div className="text-xs text-muted-foreground">{event.priceLabel}</div>
            <div className="flex items-center justify-between text-xs font-bold uppercase tracking-wider text-brand">
              <span className="truncate">{event.lockedTeaser ? "Sign in" : event.bookingNote}</span>
              <ArrowUpRight className="h-4 w-4 shrink-0 transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </div>
          </Link>
        ))}
      </div>
      <div className="grid gap-3 md:hidden">
        {events.map((event) => (
          <Link key={event.id} href={hrefFor(event.slug)} className="grid grid-cols-[60px_1fr_auto] items-center gap-3 rounded-xl border border-border bg-card p-3">
            <div className="font-display text-sm font-black leading-tight text-brand">{event.dateShort}</div>
            <div className="min-w-0">
              <div className="truncate font-display text-sm font-black">{event.title}</div>
              <div className="truncate text-[11px] text-muted-foreground">{event.timeLabel} - {event.venue}</div>
            </div>
            <ArrowUpRight className="h-4 w-4 shrink-0 text-brand" />
          </Link>
        ))}
      </div>
    </>
  );
}

export function EventCalendar({
  events,
  basePath = "/events",
  itemLabel = "event"
}: {
  events: BuzzEvent[];
  basePath?: string;
  itemLabel?: string;
}) {
  const dated = events.filter((event) => event.startsAt);
  const monthKeys = Array.from(new Set(dated.map((event) => new Date(event.startsAt!).toISOString().slice(0, 7)))).sort();
  if (monthKeys.length === 0) {
    return <div className="rounded-xl border border-dashed border-border bg-card/40 py-12 text-center text-sm text-muted-foreground">No dates to show in calendar view.</div>;
  }
  return (
    <div className="space-y-8">
      {monthKeys.map((monthKey) => <MonthGrid key={monthKey} monthKey={monthKey} events={dated.filter((event) => new Date(event.startsAt!).toISOString().startsWith(monthKey))} basePath={basePath} itemLabel={itemLabel} />)}
    </div>
  );
}

function MonthGrid({
  monthKey,
  events,
  basePath,
  itemLabel
}: {
  monthKey: string;
  events: BuzzEvent[];
  basePath: string;
  itemLabel: string;
}) {
  const [year, month] = monthKey.split("-").map(Number);
  const first = new Date(Date.UTC(year!, month! - 1, 1));
  const daysInMonth = new Date(Date.UTC(year!, month!, 0)).getUTCDate();
  const cells: (number | null)[] = Array(first.getUTCDay()).fill(null);
  for (let day = 1; day <= daysInMonth; day++) cells.push(day);
  while (cells.length % 7 !== 0) cells.push(null);
  const byDay = new Map<number, BuzzEvent[]>();
  for (const event of events) {
    const day = new Date(event.startsAt!).getUTCDate();
    byDay.set(day, [...(byDay.get(day) ?? []), event]);
  }
  const label = new Intl.DateTimeFormat("en-AU", { month: "long", year: "numeric", timeZone: "UTC" }).format(first);
  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card">
      <div className="flex items-center justify-between border-b border-border bg-ink-2/60 px-5 py-3">
        <div className="font-display text-lg font-black">{label}</div>
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">{events.length} {itemLabel}{events.length === 1 ? "" : "s"}</div>
      </div>
      <div className="grid grid-cols-7 border-b border-border bg-ink-2/30 text-center text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => <div key={day} className="px-2 py-2">{day}</div>)}
      </div>
      <div className="grid grid-cols-7">
        {cells.map((day, index) => {
          const items = day ? byDay.get(day) ?? [] : [];
          return (
            <div key={`${day}-${index}`} className={`min-h-[92px] border-b border-r border-border p-2 last:border-r-0 sm:min-h-[120px] ${day === null ? "bg-ink-2/40" : ""}`}>
              {day ? <><div className={`mb-1 font-display text-xs font-black ${items.length ? "text-brand" : "text-muted-foreground"}`}>{day}</div><div className="space-y-1">{items.map((event) => <Link key={event.id} href={`${basePath}/${event.slug}`} className="block truncate rounded-md bg-brand/15 px-1.5 py-1 text-[10px] font-bold uppercase tracking-wider text-brand hover:bg-brand hover:text-white">{event.title}</Link>)}</div></> : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function DealTable({ deals }: { deals: BuzzDeal[] }) {
  return (
    <>
      <div className="hidden overflow-hidden rounded-xl border border-border bg-card md:block">
        <div className="grid grid-cols-[1.5fr_1fr_1fr_1fr_120px] gap-4 border-b border-border bg-ink-2/60 px-5 py-3 text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
          <div>Merchant</div><div>Offer</div><div>Category</div><div>Available</div><div>From</div>
        </div>
        {deals.map((deal) => (
          <Link key={deal.id} href={`/deals/${deal.slug}`} className="group grid grid-cols-[1.5fr_1fr_1fr_1fr_120px] items-center gap-4 border-b border-border px-5 py-4 text-sm transition last:border-b-0 hover:bg-ink-2/40">
            <div className="min-w-0"><div className="font-display text-base font-black leading-tight">{deal.merchant}</div><div className="line-clamp-1 text-xs text-muted-foreground">{deal.suburb}</div></div>
            <div className="font-script text-lg leading-tight text-brand">{deal.offer}</div>
            <div className="text-xs text-muted-foreground">{deal.category}</div>
            <div className="text-xs text-muted-foreground">{deal.daysAvailable}</div>
            <div className="flex items-center justify-between text-xs font-bold uppercase tracking-wider text-brand"><span>{deal.priceFrom ?? "-"}</span><ArrowUpRight className="h-4 w-4 shrink-0 transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></div>
          </Link>
        ))}
      </div>
      <div className="grid gap-3 md:hidden">
        {deals.map((deal) => <Link key={deal.id} href={`/deals/${deal.slug}`} className="grid grid-cols-[1fr_auto] items-start gap-3 rounded-xl border border-border bg-card p-3"><div className="min-w-0"><div className="truncate font-display text-sm font-black">{deal.merchant}</div><div className="truncate text-[11px] text-muted-foreground">{deal.offer} - {deal.suburb}</div></div><ArrowUpRight className="h-4 w-4 shrink-0 text-brand" /></Link>)}
      </div>
    </>
  );
}

export function DealWeekCalendar({ deals }: { deals: BuzzDeal[] }) {
  const labels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card">
      <div className="flex items-center justify-between border-b border-border bg-ink-2/60 px-5 py-3">
        <div className="font-display text-lg font-black">This Week</div>
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">Offer availability</div>
      </div>
      <div className="grid grid-cols-7">
        {labels.map((day) => (
          <div key={day} className="min-h-[160px] border-b border-r border-border p-2 last:border-r-0">
            <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">{day}</div>
            <div className="space-y-1">
              {deals.slice(0, 8).map((deal) => <Link key={`${day}-${deal.id}`} href={`/deals/${deal.slug}`} className="block rounded-md bg-brand/15 px-1.5 py-1 text-[10px] font-bold uppercase tracking-wider text-brand hover:bg-brand hover:text-white"><span className="block truncate">{deal.merchant}</span></Link>)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function BusinessTable({ businesses }: { businesses: BuzzBusiness[] }) {
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      <div className="hidden grid-cols-[1.6fr_1.1fr_1fr_1.1fr_120px] gap-4 border-b border-border bg-ink-2/60 px-5 py-3 text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground md:grid">
        <div>Business</div><div>Category</div><div>Suburb</div><div>Phone</div><div>Website</div>
      </div>
      {businesses.map((business) => (
        <div key={business.id} className="grid gap-3 border-b border-border p-4 text-sm last:border-b-0 md:grid-cols-[1.6fr_1.1fr_1fr_1.1fr_120px] md:items-center md:gap-4 md:px-5">
          <div className="min-w-0"><Link href={`/services/directory/${business.slug}`} className="font-display text-base font-black leading-tight hover:text-brand">{business.name}</Link><div className="line-clamp-1 text-xs text-muted-foreground">{business.description}</div></div>
          <div className="text-muted-foreground">{business.subcategory ?? business.category}</div>
          <div className="text-muted-foreground">{business.suburb}</div>
          <div className="text-muted-foreground">{business.phone ?? business.email ?? "-"}</div>
          <div className="flex items-center justify-between text-xs font-bold uppercase tracking-wider text-brand md:justify-end">
            {business.websiteUrl ? <a href={business.websiteUrl} target="_blank" rel="noreferrer" className="hover:underline">Visit</a> : <Link href={`/services/directory/${business.slug}`} className="hover:underline">View</Link>}
          </div>
        </div>
      ))}
    </div>
  );
}
